import { useCallback, useEffect, useState } from "react";
import {
  enableWebPush,
  fetchFanStatuses,
  getStoredSubscriptionId,
  isIosSafari,
  isStandalonePwa,
  patchFanStatus,
  type ApplicationStatus,
  type AttendanceStatus,
  type FanStatusRow,
} from "../lib/fan-notifications-client";

export type FanNoticeMode = "deadline" | "event" | "reception";

type Props = {
  eventId: string;
  eventName: string;
  mode: FanNoticeMode;
  variant?: "full" | "notifyOnly";
  showNotifyButton?: boolean;
  /** 締切までの日数（表示用） */
  daysUntilDeadline?: number | null;
  /** 開催までの日数 */
  daysUntilEvent?: number | null;
};

const defaultStatus = (eventId: string): FanStatusRow => ({
  eventId,
  notificationEnabled: false,
  applicationStatus: "none",
  attendanceStatus: "none",
});

export function FanNoticeActions({
  eventId,
  eventName,
  mode,
  variant = "full",
  showNotifyButton = false,
  daysUntilDeadline,
  daysUntilEvent,
}: Props) {
  const [status, setStatus] = useState<FanStatusRow>(() => defaultStatus(eventId));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [showIosHint, setShowIosHint] = useState(isIosSafari() && !isStandalonePwa());

  const reload = useCallback(async () => {
    const m = await fetchFanStatuses([eventId]);
    setStatus(m[eventId] ?? defaultStatus(eventId));
  }, [eventId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const save = async (patch: {
    notificationEnabled?: boolean;
    applicationStatus?: ApplicationStatus;
    attendanceStatus?: AttendanceStatus;
    subscriptionId?: string | null;
  }) => {
    setBusy(true);
    setMsg(null);
    const next = await patchFanStatus({ eventId, ...patch });
    if (next) setStatus(next);
    setBusy(false);
    return next;
  };

  const onNotifyOn = async () => {
    setBusy(true);
    setMsg(null);
    const push = await enableWebPush();
    if (push.error) {
      setMsg(push.error);
      setBusy(false);
      return;
    }
    await save({ notificationEnabled: true, subscriptionId: push.subscriptionId ?? getStoredSubscriptionId() });
    setMsg("通知はオンです");
    setBusy(false);
  };

  const headline =
    mode === "deadline" || mode === "reception"
      ? daysUntilDeadline != null
        ? `締切まであと${daysUntilDeadline}日！`
        : "締切が近づいています！"
      : daysUntilEvent === 0
        ? "今日開催！"
        : daysUntilEvent === 1
          ? "明日開催！"
          : "まもなく開催！";
  const shouldShowNotifyButton = variant === "notifyOnly" || showNotifyButton;

  return (
    <section className={`fan-notice-actions${variant === "notifyOnly" ? " fan-notice-actions--notify-only" : ""}`} aria-label={`${eventName}の通知・回答`}>
      {variant === "full" ? <p className="fan-notice-actions-headline">{headline}</p> : null}
      {showIosHint ? (
        <p className="fan-notice-ios-hint">
          iPhoneで通知を受け取るには、このサイトをホーム画面に追加してから通知をONにしてください。
        </p>
      ) : null}
      {msg ? <p className="fan-notice-actions-msg">{msg}</p> : null}

      {shouldShowNotifyButton && (
        <div className="fan-notice-actions-row">
          {!status.notificationEnabled ? (
            <button type="button" className="btn small fan-notice-btn" disabled={busy} onClick={() => void onNotifyOn()}>
              🔔通知をオンにする！
            </button>
          ) : (
            <span className="chip active fan-notice-chip-on" aria-label={`${eventName}の通知はオンです`}>
              通知はオンです
            </span>
          )}
          {variant !== "notifyOnly" && (mode === "deadline" || mode === "reception") ? (
            <>
              <button
                type="button"
                className={`btn small secondary fan-notice-btn${status.applicationStatus === "applied" ? " active" : ""}`}
                disabled={busy}
                onClick={() => void save({ applicationStatus: "applied" as ApplicationStatus })}
              >
                申し込み完了🫶
              </button>
              <button
                type="button"
                className={`btn small secondary fan-notice-btn${status.applicationStatus === "skip" ? " active" : ""}`}
                disabled={busy}
                onClick={() => void save({ applicationStatus: "skip" as ApplicationStatus })}
              >
                今回は行かない🥲
              </button>
            </>
          ) : null}
        </div>
      )}

      {variant !== "notifyOnly" && mode === "event" && status.attendanceStatus !== "none" ? (
        <p className="fan-notice-actions-msg">
          回答済み: {status.attendanceStatus === "going" ? "行く" : status.attendanceStatus === "stream" ? "配信で見る" : status.attendanceStatus === "skip" ? "行かない" : "まだ決めてない"}
        </p>
      ) : null}
    </section>
  );
}
